import { useCallback } from "react";
import { Box, DatePicker } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

import {
  isPresetValue,
  parseISODate,
  type DatePresetCustom,
} from "#app/lib/utils/date-presets";

type QuickCustomRangeProps = Omittable<{
  value: unknown;
  onChange: (value: unknown) => void;
}>;

function atMidnight(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

/**
 * The two-date range a date filter's presets cannot enumerate, as two stacked
 * pickers — the tail of the desktop submenu and of the mobile date page alike.
 *
 * A range commits as soon as BOTH ends are set: neither surface has an OK
 * button, and a half-set range is not a filter. Either picker refuses a day in
 * the future and a day past the other end.
 */
export function QuickCustomRange({ value, onChange }: QuickCustomRangeProps) {
  const custom = isPresetValue(value) ? value.custom : undefined;

  const commit = useCallback(
    (next: DatePresetCustom) => {
      onChange(
        next.startDate && next.endDate ? { key: "custom", custom: next } : null,
      );
    },
    [onChange],
  );

  const startMatcher = useCallback(
    (date: Date) => {
      const today = atMidnight(new Date()).getTime();
      const day = atMidnight(date).getTime();
      if (day > today) return true;
      const end = parseISODate(custom?.endDate);
      return !!end && day > atMidnight(end).getTime();
    },
    [custom?.endDate],
  );

  const endMatcher = useCallback(
    (date: Date) => {
      const today = atMidnight(new Date()).getTime();
      const day = atMidnight(date).getTime();
      if (day > today) return true;
      const start = parseISODate(custom?.startDate);
      return !!start && day < atMidnight(start).getTime();
    },
    [custom?.startDate],
  );

  return (
    <Box direction="column" gap="2xs">
      <DatePicker
        value={parseISODate(custom?.startDate)}
        onValueChange={(date) =>
          commit({
            startDate: date ? date.toISOString() : "",
            endDate: custom?.endDate ?? "",
          })
        }
        placeholder="Start…"
        size="sm"
        disabledDates={startMatcher}
      />
      <DatePicker
        value={parseISODate(custom?.endDate)}
        onValueChange={(date) =>
          commit({
            startDate: custom?.startDate ?? "",
            endDate: date ? date.toISOString() : "",
          })
        }
        placeholder="End…"
        size="sm"
        disabledDates={endMatcher}
      />
    </Box>
  );
}
